/**
 * Commander store: shopping cart per reservation.
 * Keyed by reservation id so when the websocket updates a reservation,
 * the cart is not lost (it lives here, not on the reservation object).
 */

const state = {
	/** @type {Record<string | number, Array<Object>>} reservationId -> cart (same shape as menu.productsCart) */
	cartsByReservationId: {},
};

const getters = {
	/**
	 * Returns the cart for a reservation. Empty array if none.
	 * @param {Object} state
	 * @returns {function(reservationId: string|number): Array}
	 */
	getCartByReservationId: (state) => (reservationId) => {
		if (reservationId == null) return [];
		const cart = state.cartsByReservationId[reservationId];
		return Array.isArray(cart) ? cart : [];
	},
};

const mutations = {
	/**
	 * Set (or replace) the cart for a reservation.
	 * @param {Object} state
	 * @param {{ reservationId: string|number, productsCart: Array }} payload
	 */
	setCartForReservation(state, { reservationId, productsCart }) {
		if (reservationId == null) return;
		const list = Array.isArray(productsCart) ? productsCart : [];
		state.cartsByReservationId = {
			...state.cartsByReservationId,
			[reservationId]: list,
		};
	},

	/**
	 * Clear the cart for a reservation.
	 * @param {Object} state
	 * @param {string|number} reservationId
	 */
	clearCartForReservation(state, reservationId) {
		if (reservationId == null) return;
		const next = { ...state.cartsByReservationId };
		delete next[reservationId];
		state.cartsByReservationId = next;
	},

	/**
	 * Remove the reservation from the store (same as clearCartForReservation).
	 * Use when the cart is sent or no longer needed so we only keep reservations that have items in the cart.
	 * @param {Object} state
	 * @param {string|number} reservationId
	 */
	removeReservation(state, reservationId) {
		if (reservationId == null) return;
		const next = { ...state.cartsByReservationId };
		delete next[reservationId];
		state.cartsByReservationId = next;
	},
};

export default {
	namespaced: true,
	state,
	getters,
	mutations,
};
